import { useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useContext } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { ListOwnedPagesDocument } from '@/__generated__/graphql.js';
import Sidebar from '@/components/Sidebar.js';
import { Button } from '@/components/ui/button.js';
import { GrovePath } from '@/config/paths.js';
import { myhiveLocalization, MyHiveLocalizationKey } from '@/localization/my-hive.js';
import { LanguageContext } from '@/providers/LanguageProvider.js';

export default function Home(): React.JSX.Element {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: auth0UserLoading } = useAuth0();
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];
  const { data, loading: isAssetListLoading } = useQuery(ListOwnedPagesDocument, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated,
  });

  if (!auth0UserLoading && !isAuthenticated) {
    window.location.href = t[MyHiveLocalizationKey.MarketingSiteUrl];
  }
  const sortedPages = [...(data?.ownedPages ?? [])].sort((a, b) => a.title.localeCompare(b.title));
  const firstSlugInSidebar = sortedPages[0]?.slug;
  const firstSlugWithHives = sortedPages.find((item) => item.hasBeehives)?.slug;
  const someHiveHasReportingEnabled: boolean = !!data?.ownedPages.filter((item) => item.isReportingEnabled).length;

  // TYI = has reporting AND has hives ???
  const isTYI: boolean = someHiveHasReportingEnabled && !!firstSlugWithHives;

  if (someHiveHasReportingEnabled && !isTYI) {
    navigate(generatePath(GrovePath.Reporting));
  } else if (isTYI && firstSlugWithHives) {
    navigate(generatePath(GrovePath.Asset, { assetSlug: firstSlugWithHives }));
  } else if (firstSlugInSidebar) {
    navigate(generatePath(GrovePath.Asset, { assetSlug: firstSlugInSidebar || '404' }));
  }

  const supportLink =
    language === 'fr'
      ? 'https://support.alveole.buzz/hc/fr/requests/new'
      : 'https://support.alveole.buzz/hc/en-us/requests/new';

  return isAssetListLoading ||
    auth0UserLoading ||
    !isAuthenticated ||
    someHiveHasReportingEnabled ||
    firstSlugInSidebar ? (
    <div className="bg-sky-50/50 w-screen h-screen" />
  ) : (
    <Sidebar>
      <div className="flex flex-col gap-5 p-5 md:m-60 md:w-1/2 w-full">
        <h1 className="text-4xl md:text-5xl font-semibold text-alveole-gold">
          {t[MyHiveLocalizationKey.WelcomeToMyhive]}
        </h1>
        <p className="text-sm text-gray-600">{t[MyHiveLocalizationKey.HeroDescription]}</p>
        <Button
          id="need-support-button"
          className="bg-alveole-green cursor-pointer w-fit"
          onClick={() => window.open(supportLink, '_blank')}
        >
          {t[MyHiveLocalizationKey.NeedSupport]}
        </Button>
      </div>
    </Sidebar>
  );
}
